import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import {
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Wand2, Edit3, Loader2, CheckCircle, Target, FileEdit, TrendingUp, Zap, Ruler,
  Briefcase, Calculator, PenTool, Search, Award, Save, RotateCcw, Eye,
  Undo2, Redo2, Settings, X, Check, Mic, MicOff
} from 'lucide-react';
import { fitResumeLines } from '@/lib/measuredFit';
import { FineTunePanel } from '@/components/FineTunePanel';
import { LayoutPanel } from '@/components/resume/LayoutPanel';
import { PageEditor } from '@/components/resume/PageEditor';
import { HistoryPanel } from '@/components/resume/HistoryPanel';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { WordOnlineViewer } from '@/components/WordOnlineViewer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { TipTapResumeEditor, TipTapResumeEditorHandle } from '@/components/TipTapResumeEditor';
// Mammoth lazy-loaded inside an effect (~80kb gzipped). Used to convert the
// rendered .docx into HTML so the WYSIWYG editor inherits the actual Word
// formatting (bold, headings, lists, tables) instead of showing flat text.
// deno-lint-ignore no-explicit-any
type MammothConvertResult = { value: string; messages: Array<{ message: string; type: string }> };

const AI_AUTO_APPLY_KEY = 'tailorwiz_ai_auto_apply';
const AUTO_FORMAT_ON_SAVE_KEY = 'tailorwiz_auto_format_on_save';
const MAX_HISTORY = 50; // Cap undo/redo stacks so memory doesn't grow forever

interface DocumentEditingViewProps {
  documentContent: string;
  documentJson?: any;
  documentType?: string;
  fileUrl?: string;
  fileName?: string;
  onContentUpdate: (newContent: string) => void;
  onJsonUpdate?: (newJson: any) => void;
  onSaveAndRegenerate?: () => Promise<void>;
  /**
   * Preferred regenerate path: caller passes the latest JSON explicitly so
   * regen uses the user's most recent edits (rather than the stale
   * `tab.resumeJson` captured by the standard `onSaveAndRegenerate` closure).
   * When provided, the editor uses this for every auto-save and manual save.
   */
  onSaveAndRegenerateWithJson?: (newJson: any) => Promise<void>;
  /**
   * Optional resume-only handler. When provided, the "Smart Formatting"
   * quick action re-renders the resume through the page-fit engine
   * (keeps the first job on page 1) instead of doing a plain text edit.
   */
  onSmartFormat?: () => Promise<void>;
  /**
   * Optional Docmosis-template hint that styles the WYSIWYG page (font /
   * accent color) to roughly mirror the chosen template family. Purely
   * visual — does not change content, JSON, or the save pipeline.
   */
  templateFamily?: 'classic' | 'navy-serif' | 'professional' | 'burgundy' | 'charcoal-gold' | 'minimal' | 'slate-sans';
  /**
   * The Docmosis template id currently in use. Used by the "Format Resume"
   * button so the format-rules engine can pick the right per-template
   * character budget for highlights, skill-column count, etc.
   */
  docmosisTemplateId?: string;
  /** Target job description, used by the AI Assistant for keyword edits. */
  jobDescription?: string;
  /** The saved session, for version history. */
  tailoredRowId?: string;
  /** Pages in the last render, from the page estimate. */
  pageCount?: number;
  onClose: () => void;
  className?: string;
}

interface PredefinedAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

export const DocumentEditingView: React.FC<DocumentEditingViewProps> = ({
  documentContent,
  documentJson,
  documentType = 'resume',
  fileUrl,
  fileName,
  onContentUpdate,
  onJsonUpdate,
  onSaveAndRegenerate,
  onSaveAndRegenerateWithJson,
  onSmartFormat,
  templateFamily,
  docmosisTemplateId,
  jobDescription,
  tailoredRowId,
  pageCount,
  onClose,
  className
}) => {
  const { toast } = useToast();
  const editorRef = useRef<HTMLDivElement>(null);
  // Imperative handle for the Word-styled TipTap editor (Bold/Italic/lists/
  // align/undo/redo all flow through this so we keep one toolbar code path).
  const tiptapRef = useRef<TipTapResumeEditorHandle | null>(null);
  const [editedContent, setEditedContent] = useState(documentContent);
  const editedContentRef = useRef(documentContent);
  editedContentRef.current = editedContent;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastEdit, setLastEdit] = useState<{ summary: string; reasoning: string; questions?: string[] } | null>(null);
  const [customRequest, setCustomRequest] = useState('');
  // The conversation with the AI Assistant (request + its reply), so follow-ups
  // like "make that shorter" know what "that" is.
  const [chatHistory, setChatHistory] = useState<Array<{ request: string; reply: string }>>([]);
  const [processingNote, setProcessingNote] = useState('');
  // Talk to the assistant: browser speech recognition fills the request box.
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const speechSupported = typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  const toggleListening = () => {
    if (isListening) { recognitionRef.current?.stop(); return; }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    const base = customRequest.trim() ? customRequest.trim() + ' ' : '';
    rec.onresult = (ev: any) => {
      let text = '';
      for (let i = 0; i < ev.results.length; i++) text += ev.results[i][0].transcript;
      setCustomRequest(base + text.trim());
    };
    rec.onerror = (ev: any) => {
      setIsListening(false);
      if (ev?.error === 'not-allowed') toast({ title: 'Microphone blocked', description: 'Allow microphone access for tailorwiz.com in your browser, then tap the mic again.', variant: 'destructive' });
    };
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    rec.start();
    setIsListening(true);
  };
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Default to preview when a rendered Word doc URL is available; only fall
  // back to the plain-text editor when there's nothing to preview yet. This
  // matches what users expect when they click "Edit" on a finished doc.
  // Resumes are edited through the AI Assistant, which keeps the real design.
  // The plain text page lost all formatting and its typing never reached the
  // resume, so it is letters only (Marcus, Nov 3 2026).
  const isResumeView = !['cover-letter', 'thank-you-letter', 'outreach-letter', 'executive-bio', 'linkedin-profile']
    .includes((documentType || 'resume').toLowerCase().replace(/_/g, '-'));
  const [viewMode, setViewMode] = useState<'editor' | 'preview'>(fileUrl || isResumeView ? 'preview' : 'editor');

  // Resume data being edited. Every change goes through commitJson, which keeps
  // undo history and saves itself, so nothing depends on a Save button.
  const [liveJson, setLiveJson] = useState<any>(documentJson);
  const liveRef = useRef<any>(documentJson);
  const dirtyRef = useRef(false);
  useEffect(() => { if (!dirtyRef.current) { setLiveJson(documentJson); liveRef.current = documentJson; } }, [documentJson]);
  const [jsonPast, setJsonPast] = useState<any[]>([]);
  const [jsonFuture, setJsonFuture] = useState<any[]>([]);
  const [leftView, setLeftView] = useState<'preview' | 'page'>('preview');
  const [saveState, setSaveState] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const saveTimer = useRef<number | null>(null);
  const autoSaveRef = useRef<((text: string, o?: any) => Promise<void>) | null>(null);
  const showJson = (next: any) => { liveRef.current = next; setLiveJson(next); };
  // One save at a time, newest last, so an older save can never land on top of a newer one.
  const savingRef = useRef(false);
  const queuedRef = useRef<any>(null);
  const persistJson = async (next: any) => {
    if (savingRef.current) { queuedRef.current = next; return; }
    savingRef.current = true;
    setSaveState('saving');
    try {
      await autoSaveRef.current?.(editedContentRef.current, { silent: true, presetJson: next, noHistory: true });
      if (liveRef.current === next) dirtyRef.current = false;
      setSaveState('saved');
    } catch (e) {
      console.error('Autosave failed:', e);
      setSaveState('error');
    } finally {
      savingRef.current = false;
      const queued = queuedRef.current;
      queuedRef.current = null;
      if (queued) persistJson(queued);
    }
  };
  const flushSave = () => {
    if (!saveTimer.current) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = null;
    if (dirtyRef.current) persistJson(liveRef.current);
  };
  const commitJson = (next: any) => {
    setJsonPast((p) => [...p.slice(-49), liveRef.current]);
    setJsonFuture([]);
    showJson(next);
    dirtyRef.current = true;
    setSaveState('pending');
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { saveTimer.current = null; persistJson(liveRef.current); }, 2000);
  };
  const stepHistory = async (back: boolean) => {
    const from = back ? jsonPast : jsonFuture;
    if (!from.length) return;
    const target = back ? from[from.length - 1] : from[0];
    const current = liveRef.current;
    if (back) { setJsonPast((p) => p.slice(0, -1)); setJsonFuture((f) => [current, ...f]); }
    else { setJsonFuture((f) => f.slice(1)); setJsonPast((p) => [...p, current]); }
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    showJson(target);
    dirtyRef.current = true;
    await persistJson(target);
  };

  // Undo / Redo stacks of `editedContent` snapshots. Pushes happen on AI
  // applies, manual toolbar formatting, and when the user pauses typing.
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const isApplyingHistoryRef = useRef(false);
  const typingDebounceRef = useRef<number | null>(null);

  // Mirror TipTap's internal history availability into React state so the
  // Undo / Redo buttons can be disabled/enabled correctly. TipTap pushes
  // these flags via the `onHistoryChange` callback on every transaction.
  const [tipTapCanUndo, setTipTapCanUndo] = useState(false);
  const [tipTapCanRedo, setTipTapCanRedo] = useState(false);

  // AI approval setting (persisted in localStorage). When false (default), an
  // AI edit returns a pending proposal the user must Apply or Reject. When
  // true, the AI edit is applied immediately.
  const [aiAutoApply, setAiAutoApplyState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AI_AUTO_APPLY_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const setAiAutoApply = (v: boolean) => {
    setAiAutoApplyState(v);
    try { localStorage.setItem(AI_AUTO_APPLY_KEY, v ? 'true' : 'false'); } catch { /* ignore */ }
  };

  // "Auto-format on save" preference. When on, every manual save runs the
  // resume formatting rules (highlights → 2 lines, skills → clean grid)
  // BEFORE regenerating the Docmosis document.
  const [autoFormatOnSave, setAutoFormatOnSaveState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTO_FORMAT_ON_SAVE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const setAutoFormatOnSave = (v: boolean) => {
    setAutoFormatOnSaveState(v);
    try { localStorage.setItem(AUTO_FORMAT_ON_SAVE_KEY, v ? 'true' : 'false'); } catch { /* ignore */ }
  };

  // Tracks whether a formatter run is in flight (drives the button spinner).
  const [isFormatting, setIsFormatting] = useState(false);

  // HTML version of the rendered .docx — populated by Mammoth when the
  // current `fileUrl` changes. When present, the WYSIWYG editor uses it as
  // its content instead of plain-text-with-fake-paragraphs so the visible
  // page actually matches the Word doc the user sees in preview.
  const [docHtml, setDocHtml] = useState<string>('');
  const [isLoadingDocHtml, setIsLoadingDocHtml] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!fileUrl) {
      setDocHtml('');
      return;
    }
    setIsLoadingDocHtml(true);
    (async () => {
      try {
        const mammoth = await import('mammoth/mammoth.browser');
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error(`fetch failed ${res.status}`);
        const arrayBuffer = await res.arrayBuffer();
        // deno-lint-ignore no-explicit-any
        const out: MammothConvertResult = await (mammoth as any).convertToHtml(
          { arrayBuffer },
          {
            // Mammoth's default style map already covers bold/italic/heading;
            // we just hint that "Heading 2"-style paragraphs should become
            // <h2> (section headers like SUMMARY / EXPERIENCE).
            styleMap: [
              "p[style-name='Heading 1'] => h1:fresh",
              "p[style-name='Heading 2'] => h2:fresh",
              "p[style-name='Heading 3'] => h3:fresh",
            ],
          },
        );
        if (!cancelled) {
          setDocHtml(out.value || '');
        }
      } catch (err) {
        console.warn('[DocumentEditingView] Mammoth convert failed; falling back to plain text:', err);
        if (!cancelled) setDocHtml('');
      } finally {
        if (!cancelled) setIsLoadingDocHtml(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fileUrl]);

  // A pending AI proposal awaiting user approval. Set after `processAIEdit`
  // when `aiAutoApply` is false. The preview pane shows the proposed text
  // until the user clicks Apply or Reject.
  const [pendingAiEdit, setPendingAiEdit] = useState<{
    proposedContent: string;
    proposedJson: any | null;
    summary: string;
    reasoning: string;
    previousContent: string;
  } | null>(null);

  // Push the CURRENT editedContent onto the undo stack and clear the redo
  // stack. Used right before any change overwrites editedContent.
  const pushHistory = useCallback((snapshot: string) => {
    if (isApplyingHistoryRef.current) return;
    setUndoStack((prev) => {
      if (prev[prev.length - 1] === snapshot) return prev;
      const next = [...prev, snapshot];
      return next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
    });
    setRedoStack([]);
  }, []);

  const applyContentFromHistory = (next: string) => {
    isApplyingHistoryRef.current = true;
    setEditedContent(next);
    if (editorRef.current) editorRef.current.innerText = next;
    onContentUpdate(next);
    setHasUnsavedChanges(true);
    // Release the flag on next tick so the input handler doesn't re-snapshot.
    setTimeout(() => { isApplyingHistoryRef.current = false; }, 0);
  };

  // Tracks whether the most recent undo/redo happened from Preview mode, so
  // we know to auto-save & refresh the rendered Word doc afterwards. (Undo
  // from Editor mode shows the change visually without needing a regen.)
  const triggerRefreshAfterHistory = useCallback(async () => {
    if (viewMode !== 'preview') return;
    try {
      // Use the current editedContent state; it'll be updated by the time
      // this runs (TipTap fires onChange before this is invoked).
      await handleSaveAndRegenerate({ silent: true });
    } catch {
      // handleSaveAndRegenerate already surfaces its own error toast.
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUndo = async () => {
    if (isResumeView) { await stepHistory(true); return; }
    // Prefer TipTap's native undo (preserves formatting + cursor position).
    // Falls through to the snapshot stack for content swapped in via AI
    // auto-apply where TipTap may not have a history entry.
    const editor = tiptapRef.current?.getEditor();
    if (editor && editor.can().undo()) {
      editor.chain().focus().undo().run();
      await triggerRefreshAfterHistory();
      return;
    }
    let didApply = false;
    setUndoStack((prevUndo) => {
      if (prevUndo.length === 0) return prevUndo;
      const last = prevUndo[prevUndo.length - 1];
      setRedoStack((r) => [...r, editedContent]);
      applyContentFromHistory(last);
      didApply = true;
      return prevUndo.slice(0, -1);
    });
    if (didApply) await triggerRefreshAfterHistory();
  };

  const handleRedo = async () => {
    if (isResumeView) { await stepHistory(false); return; }
    const editor = tiptapRef.current?.getEditor();
    if (editor && editor.can().redo()) {
      editor.chain().focus().redo().run();
      await triggerRefreshAfterHistory();
      return;
    }
    let didApply = false;
    setRedoStack((prevRedo) => {
      if (prevRedo.length === 0) return prevRedo;
      const last = prevRedo[prevRedo.length - 1];
      setUndoStack((u) => [...u, editedContent]);
      applyContentFromHistory(last);
      didApply = true;
      return prevRedo.slice(0, -1);
    });
    if (didApply) await triggerRefreshAfterHistory();
  };

  // Debug: log what content we receive
  console.log('[DocumentEditingView] documentContent length:', documentContent?.length, 'first 100 chars:', documentContent?.substring(0, 100));

  // Use a callback ref to set content when the div mounts
  const setEditorRef = useCallback((node: HTMLDivElement | null) => {
    (editorRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (node) {
      // Always set content when the node mounts
      const content = editedContent || documentContent || '';
      console.log('[DocumentEditingView] Setting editor content on mount, length:', content.length);
      node.innerText = content;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync content when documentContent prop changes (e.g. after AI edit)
  useEffect(() => {
    setEditedContent(documentContent);
    if (editorRef.current) {
      editorRef.current.innerText = documentContent;
    }
  }, [documentContent]);

  const predefinedActions: PredefinedAction[] = [
    { id: 'REMOVE_REDUNDANCIES', label: 'Remove Redundancies', icon: <Target className="w-4 h-4" />, color: 'bg-red-500' },
    { id: 'SMART_FORMATTING', label: 'Smart Formatting', icon: <FileEdit className="w-4 h-4" />, color: 'bg-blue-500' },
    { id: 'REMOVE_GAPS', label: 'Remove Gaps', icon: <TrendingUp className="w-4 h-4" />, color: 'bg-orange-500' },
    { id: 'HIGHLIGHT_TRANSFERABLE', label: 'Transferable Skills', icon: <Zap className="w-4 h-4" />, color: 'bg-purple-500' },
    { id: 'ADJUST_LENGTH', label: 'Adjust Length', icon: <Ruler className="w-4 h-4" />, color: 'bg-green-500' },
    { id: 'OPTIMIZE_WORK_HISTORY', label: 'Optimize Work History', icon: <Briefcase className="w-4 h-4" />, color: 'bg-indigo-500' },
    { id: 'ADD_METRICS', label: 'Add Metrics', icon: <Calculator className="w-4 h-4" />, color: 'bg-cyan-500' },
    { id: 'CHANGE_TONE', label: 'Change Tone', icon: <PenTool className="w-4 h-4" />, color: 'bg-pink-500' },
    { id: 'ENHANCE_KEYWORDS', label: 'Enhance Keywords', icon: <Search className="w-4 h-4" />, color: 'bg-yellow-500' },
    { id: 'STRENGTHEN_ACHIEVEMENTS', label: 'Strengthen Achievements', icon: <Award className="w-4 h-4" />, color: 'bg-emerald-500' },
  ];

  const formatText = (command: string) => {
    // Snapshot before applying a formatting command so it can be undone.
    pushHistory(editedContent);
    // Translate the legacy execCommand names the toolbar uses into TipTap
    // commands. The TipTap editor reports back via onChange → setEditedContent.
    const map: Record<string, string> = {
      bold: 'bold',
      italic: 'italic',
      underline: 'underline',
      insertUnorderedList: 'bulletList',
      insertOrderedList: 'orderedList',
      justifyLeft: 'alignLeft',
      justifyCenter: 'alignCenter',
      justifyRight: 'alignRight',
    };
    const tiptapCmd = map[command];
    if (tiptapCmd && tiptapRef.current) {
      tiptapRef.current.command(tiptapCmd as any);
      return;
    }
    // Fallback for any unmapped command — should not normally fire.
    if (editorRef.current) {
      document.execCommand(command, false);
      const newContent = editorRef.current.innerText;
      setEditedContent(newContent);
      setHasUnsavedChanges(true);
      onContentUpdate(newContent);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerText;
      // Debounce-snapshot the prior content when the user pauses typing, so
      // a long edit run collapses into one undo step instead of one per char.
      if (!isApplyingHistoryRef.current) {
        if (typingDebounceRef.current) window.clearTimeout(typingDebounceRef.current);
        const beforeEdit = editedContent;
        typingDebounceRef.current = window.setTimeout(() => {
          pushHistory(beforeEdit);
        }, 600);
      }
      setEditedContent(newContent);
      setHasUnsavedChanges(true);
      onContentUpdate(newContent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey) {
      // Intercept Ctrl/Cmd+Z and Ctrl/Cmd+Y / Ctrl+Shift+Z so they drive OUR
      // history stack instead of the browser's contentEditable native one,
      // which doesn't know about AI edits.
      // While typing on the page, Ctrl+Z undoes the typing itself.
      if ((e.target as HTMLElement)?.closest?.('[data-page-text]')) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); return; }
      if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); handleRedo(); return; }
      switch (e.key) {
        case 'b': e.preventDefault(); formatText('bold'); break;
        case 'i': e.preventDefault(); formatText('italic'); break;
        case 'u': e.preventDefault(); formatText('underline'); break;
      }
    }
  };

  const isResumeDoc = !['cover-letter', 'thank-you-letter', 'outreach-letter', 'executive-bio', 'linkedin-profile']
    .includes((documentType || 'resume').toLowerCase().replace(/_/g, '-'));

  const processAIEdit = async (userRequest: string) => {
    if (recognitionRef.current && isListening) recognitionRef.current.stop();
    const label = predefinedActions.find((a) => a.id === userRequest)?.label ?? userRequest;
    setIsProcessing(true);
    setProcessingNote('Making your change…');
    setLastEdit(null);
    setPendingAiEdit(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-document-editor', {
        body: {
          userRequest,
          documentContent: editedContent,
          documentJson: documentJson || undefined,
          documentType,
          jobDescription: jobDescription || undefined,
          history: chatHistory.slice(-6),
        },
      });
      if (error) throw error;
      if (!data?.success) {
        toast({ title: "Sorry, that change couldn't be made", description: data?.error || 'Please try again in a few minutes.', variant: 'destructive' });
        return;
      }
      const summary: string = data.changesSummary || 'Done.';
      const reasoning: string = data.changeReasoning || '';
      const questions: string[] = Array.isArray(data.questions) ? data.questions : [];
      setChatHistory((h) => [...h, { request: label, reply: summary + (questions.length ? ` Questions: ${questions.join(' ')}` : '') }]);
      setCustomRequest('');

      // A reply with no change (a question back, or nothing needed).
      if (data.editedJson && data.changed === false) {
        setLastEdit({ summary, reasoning, questions });
        return;
      }

      let proposedJson = data.editedJson || null;
      // New summary wording needs a new margin: a hand-set one no longer applies.
      if (proposedJson && proposedJson.Summary !== documentJson?.Summary) delete proposedJson.SummaryIndentTwips;
      // Every edited line is measured and fitted to the RDR line rules
      // (summary rectangle, highlights 2 full lines, bullets 1 line).
      if (proposedJson && isResumeDoc) {
        setProcessingNote('Fitting the edited lines to the RDR line rules…');
        try {
          const fit = await fitResumeLines(proposedJson, documentJson);
          proposedJson = fit.json;
        } catch (fitErr) {
          console.error('Line fit after AI edit failed:', fitErr);
          toast({ title: 'Edit made, line fitting skipped', description: 'Use Smart Formatting to fit the lines when you are done editing.' });
        }
      }
      const previousContent = editedContent;
      const proposed = data.editedContent || editedContent;

      if (aiAutoApply) {
        pushHistory(previousContent);
        setEditedContent(proposed);
        if (editorRef.current) editorRef.current.innerText = proposed;
        setHasUnsavedChanges(true);
        onContentUpdate(proposed);
        setLastEdit({ summary, reasoning, questions });
        setProcessingNote('Updating your Word document…');
        try {
          await autoSaveWithText(proposed, { silent: true, presetJson: proposedJson || undefined });
          toast({ title: 'Change made', description: summary });
        } catch (err: any) {
          toast({ title: "Sorry, the document couldn't be updated", description: 'Click "Save & Update Word Doc" to try again.', variant: 'destructive' });
        }
      } else {
        setPendingAiEdit({ proposedContent: proposed, proposedJson, summary: summary + (questions.length ? ` ${questions.join(' ')}` : ''), reasoning, previousContent });
        toast({ title: 'Change ready', description: 'Review it and click Apply Changes.' });
      }
    } catch (error: any) {
      console.error('Error processing AI edit:', error);
      toast({ title: "Sorry, that change couldn't be made right now", description: 'Please try again in a few minutes.', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
      setProcessingNote('');
    }
  };

  const acceptPendingAiEdit = async () => {
    if (!pendingAiEdit) return;
    const { proposedContent, proposedJson, summary, reasoning, previousContent } = pendingAiEdit;
    pushHistory(previousContent);
    setEditedContent(proposedContent);
    if (editorRef.current) editorRef.current.innerText = proposedContent;
    setHasUnsavedChanges(true);
    onContentUpdate(proposedContent);
    setLastEdit({ summary, reasoning });
    setPendingAiEdit(null);
    toast({ title: 'AI changes applied — regenerating document…', description: summary });
    // Auto-save & refresh preview so the Word doc reflects the new text.
    try {
      await autoSaveWithText(proposedContent, { silent: true, presetJson: proposedJson || undefined });
      toast({ title: 'Document refreshed', description: 'AI changes are live in the preview.' });
    } catch (err: any) {
      toast({ title: 'Auto-save after AI edit failed', description: err?.message || 'Click "Save & Update Word Doc" to retry.', variant: 'destructive' });
    }
  };

  const rejectPendingAiEdit = () => {
    if (!pendingAiEdit) return;
    setPendingAiEdit(null);
    toast({ title: 'AI changes rejected', description: 'The document was not modified.' });
  };

  // (runResumeFormatter is declared lower down, after autoSaveWithText, to
  // avoid a TDZ ReferenceError on its dependency array.)

  // Smart Formatting for resumes: re-render through the page-fit engine so the
  // first job is condensed only as much as needed to land on page 1.
  const handleSmartFormat = async () => {
    if (isResumeDoc && documentJson) {
      setIsProcessing(true);
      setProcessingNote('Measuring every line and fitting it to the RDR line rules…');
      setLastEdit(null);
      try {
        const fit = await fitResumeLines(documentJson);
        if (fit.json?.Summary !== documentJson?.Summary) delete fit.json.SummaryIndentTwips;
        setProcessingNote('Updating your Word document…');
        await autoSaveWithText(editedContent, { silent: true, presetJson: fit.json });
        const s = fit.fixed === 0 && fit.unresolved === 0
          ? 'Every line already fits: summary rectangle, two-line highlights and one-line bullets.'
          : `${fit.fixed} line${fit.fixed === 1 ? '' : 's'} refitted to the RDR line rules${fit.unresolved ? `, ${fit.unresolved} still close but not exact` : ''}.`;
        setLastEdit({ summary: s, reasoning: '' });
        setChatHistory((h) => [...h, { request: 'Smart Formatting', reply: s }]);
        toast({ title: 'Smart Formatting done', description: s });
      } catch (error: any) {
        console.error('Smart formatting failed:', error);
        toast({ title: "Sorry, we couldn't reformat your resume right now", description: 'Please try again in a few minutes.', variant: 'destructive' });
      } finally {
        setIsProcessing(false);
        setProcessingNote('');
      }
      return;
    }
    if (!onSmartFormat) {
      // No resume re-render available (e.g. letters) — fall back to text edit.
      return processAIEdit('SMART_FORMATTING');
    }
    setIsProcessing(true);
    setLastEdit(null);
    try {
      await onSmartFormat();
      setLastEdit({
        summary: 'Reformatted so the first job stays on page 1.',
        reasoning: 'The resume was re-rendered through the page-fit engine. If the first job spilled onto page 2, the summary, career highlights, and first-job bullets were tightened — without dropping any facts — until it fit on page 1.',
      });
      toast({ title: 'Smart Formatting complete', description: 'First job fitted to page 1.' });
    } catch (error: any) {
      console.error('Error during smart formatting:', error);
      toast({ title: 'Smart Formatting failed', description: error.message || 'Failed to reformat the document', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Map the edited plain text back into the structured document JSON that
   * Docmosis expects. Resumes go through the `parse-resume-to-json` edge
   * function. Letters (cover, thank-you, outreach, exec bio) treat the
   * edited text as the body. LinkedIn and unknown types fall back to the
   * existing JSON so regeneration doesn't break.
   */
  const syncEditedTextToJson = useCallback(async (text: string): Promise<any> => {
    const type = (documentType || 'resume').toLowerCase().replace(/_/g, '-');
    const isLetter = ['cover-letter', 'thank-you-letter', 'outreach-letter', 'executive-bio'].includes(type);

    if (isLetter) {
      // The letter editor's plain text IS the body. Keep header info intact.
      return { ...(documentJson || {}), Body: text };
    }

    // For resumes we DO NOT re-parse arbitrary plain text back into JSON —
    // that path was dropping Skills, Experience, Career Highlights, etc.
    // Manual text edits to a resume now just pass through the original
    // structured JSON (so regen still produces the resume), and any deeper
    // structural change must go through the AI editor (which now returns
    // editedJson directly). If you typed a manual text edit and want it in
    // the doc, save again after running an AI pass over it.
    return documentJson;
  }, [documentJson, documentType]);

  /**
   * Save-and-regenerate using a specific text snapshot instead of the
   * `editedContent` state (which may not have flushed yet right after a
   * setState call). Used by the AI-edit auto-save path so the regenerated
   * document is guaranteed to contain the new text.
   */
  const autoSaveWithText = useCallback(async (text: string, options?: { silent?: boolean; presetJson?: any; noHistory?: boolean }) => {
    if (!onSaveAndRegenerate && !onSaveAndRegenerateWithJson) return;
    if (options?.presetJson && !options.noHistory && isResumeView) {
      // AI edits, Fine-Tune and Layout land here: they join the same undo history.
      setJsonPast((p) => [...p.slice(-49), liveRef.current]);
      setJsonFuture([]);
      showJson(options.presetJson);
    }
    setIsSaving(true);
    try {
      // Prefer the JSON the AI handed us directly (when it edited the
      // structure, no re-parsing required). Only fall back to the lossy
      // parse-from-text path when no preset JSON is available.
      const newJson = options?.presetJson ?? await syncEditedTextToJson(text);
      if (onJsonUpdate && newJson) onJsonUpdate(newJson);
      if (onSaveAndRegenerateWithJson && newJson) {
        await onSaveAndRegenerateWithJson(newJson);
      } else if (onSaveAndRegenerate) {
        await onSaveAndRegenerate();
      }
      setHasUnsavedChanges(false);
      setViewMode('preview');
      if (!options?.silent) {
        toast({ title: 'Document Updated', description: 'Changes saved and document regenerated. Showing preview.' });
      }
    } finally {
      setIsSaving(false);
    }
  }, [onJsonUpdate, onSaveAndRegenerate, onSaveAndRegenerateWithJson, syncEditedTextToJson, toast]);
  autoSaveRef.current = autoSaveWithText;

  /**
   * Run the resume formatter (highlights → 2 lines, skills → clean grid,
   * summary check). On success, regenerate the Docmosis document with the
   * formatted JSON so the preview reflects the cleaned-up output. Returns
   * the formatted JSON (or null on failure) so the auto-format-on-save path
   * can chain into the regen without firing twice. Declared AFTER
   * `autoSaveWithText` because it depends on it (was a TDZ bug — moving
   * this above `autoSaveWithText` produced a blank-page ReferenceError).
   */
  const runResumeFormatter = useCallback(async (
    options?: { silent?: boolean; alsoSaveAndRefresh?: boolean },
  ): Promise<any | null> => {
    if (!documentJson) {
      if (!options?.silent) {
        toast({ title: 'No structured resume data', description: 'Open a tailored resume to use the formatter.', variant: 'destructive' });
      }
      return null;
    }
    setIsFormatting(true);
    const failsafe = window.setTimeout(() => {
      setIsFormatting(false);
      toast({
        title: 'Formatter took too long',
        description: 'Try again or save first.',
        variant: 'destructive',
      });
    }, 120_000);
    try {
      const { data, error } = await supabase.functions.invoke('format-resume-rules', {
        body: {
          resumeJson: documentJson,
          templateId: docmosisTemplateId || 'template3',
          options: { fixHighlights: true, fixSkills: true },
        },
      });
      window.clearTimeout(failsafe);
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Format failed');

      const formattedJson = data.formattedJson;
      const report = data.report || {};
      const highlightsFixed = report?.highlights?.fixed_count ?? 0;
      const skillsBefore = report?.skills?.before ?? 0;
      const skillsAfter = report?.skills?.after ?? 0;
      const skillsRemoved = Array.isArray(report?.skills?.removed) ? report.skills.removed : [];
      const summaryFlagged = !!report?.summary?.template_needs_justification_fix;

      // Persist to the parent so manual saves use the cleaned JSON.
      if (onJsonUpdate) onJsonUpdate(formattedJson);

      // Compose a human-readable summary.
      const parts: string[] = [];
      parts.push(highlightsFixed > 0 ? `${highlightsFixed} highlight${highlightsFixed > 1 ? 's' : ''} rewritten to 2 lines` : 'highlights already on 2 lines');
      if (skillsRemoved.length > 0) {
        parts.push(`skills trimmed ${skillsBefore} → ${skillsAfter} (removed: ${skillsRemoved.slice(0, 3).join(', ')}${skillsRemoved.length > 3 ? '…' : ''})`);
      } else {
        parts.push('skills already fit the grid');
      }
      if (summaryFlagged) parts.push('summary: template needs a one-time justification tweak');

      if (!options?.silent) {
        toast({ title: 'Resume formatted', description: parts.join(' · ') });
      }

      if (options?.alsoSaveAndRefresh !== false) {
        try {
          await autoSaveWithText(editedContent, { silent: true, presetJson: formattedJson });
          if (!options?.silent) {
            toast({ title: 'Preview refreshed', description: 'Formatted document is now live in the preview.' });
          }
        } catch (err: any) {
          toast({ title: 'Refresh after format failed', description: err?.message || 'Click Save to retry.', variant: 'destructive' });
        }
      }
      return formattedJson;
    } catch (err: any) {
      window.clearTimeout(failsafe);
      console.error('[DocumentEditingView] runResumeFormatter failed:', err);
      if (!options?.silent) {
        toast({ title: 'Formatter failed', description: err?.message || 'Could not run the resume formatter.', variant: 'destructive' });
      }
      return null;
    } finally {
      window.clearTimeout(failsafe);
      setIsFormatting(false);
    }
  }, [documentJson, docmosisTemplateId, onJsonUpdate, autoSaveWithText, editedContent, toast]);

  const handleSaveAndRegenerate = useCallback(async (options?: { silent?: boolean }) => {
    if (!onSaveAndRegenerate && !onSaveAndRegenerateWithJson) return;
    setIsSaving(true);
    try {
      let newJson = await syncEditedTextToJson(editedContent);

      // When auto-format-on-save is on, run the formatter BEFORE regen so the
      // rendered Word doc honors the rules without a second round trip.
      if (autoFormatOnSave && newJson) {
        const formatted = await runResumeFormatter({ silent: true, alsoSaveAndRefresh: false });
        if (formatted) newJson = formatted;
      }

      if (onJsonUpdate && newJson) onJsonUpdate(newJson);

      if (onSaveAndRegenerateWithJson && newJson) {
        await onSaveAndRegenerateWithJson(newJson);
      } else if (onSaveAndRegenerate) {
        await onSaveAndRegenerate();
      }
      setHasUnsavedChanges(false);
      setViewMode('preview');
      if (!options?.silent) {
        toast({
          title: 'Document Updated',
          description: autoFormatOnSave
            ? 'Auto-formatted, saved, and regenerated. Showing preview.'
            : 'Changes saved and document regenerated. Showing preview.',
        });
      }
    } catch (error: any) {
      toast({ title: 'Save Failed', description: error.message || 'Failed to save changes', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  }, [editedContent, onJsonUpdate, onSaveAndRegenerate, onSaveAndRegenerateWithJson, syncEditedTextToJson, toast, autoFormatOnSave, runResumeFormatter]);

  const handleResetContent = () => {
    setEditedContent(documentContent);
    if (editorRef.current) {
      editorRef.current.innerText = documentContent;
    }
    setHasUnsavedChanges(false);
  };

  // Word count
  const wordCount = editedContent.split(/\s+/).filter(w => w.length > 0).length;

  return (
    <div className={`h-full flex flex-col ${className}`}>
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Left Panel - Visual Editor */}
        <ResizablePanel defaultSize={60} minSize={40}>
          <div className="h-full flex flex-col">
            {/* View Mode Toggle + Formatting Toolbar */}
            <div className="border-b bg-muted/30 px-4 py-2 flex items-center gap-1 flex-wrap">
              {/* Editor / Preview Toggle — left side. "Manual Edit (Word view)"
                  opens the WYSIWYG TipTap page styled like the chosen template;
                  "Preview" shows the rendered Word document in a read-only
                  iframe. Both flow into the same save & regen pipeline. */}
              {isResumeView && (
                <div className="flex items-center gap-1 mr-3">
                  <Button variant={leftView === 'preview' ? 'default' : 'ghost'} size="sm" className="h-8 text-xs" onClick={() => { flushSave(); setLeftView('preview'); }}>
                    <Eye className="h-3 w-3 mr-1" /> Word preview
                  </Button>
                  <Button variant={leftView === 'page' ? 'default' : 'ghost'} size="sm" className="h-8 text-xs" onClick={() => setLeftView('page')}>
                    <Edit3 className="h-3 w-3 mr-1" /> Edit on page
                  </Button>
                </div>
              )}
              {!isResumeView && <div className="flex items-center gap-1 mr-3">
                <Button
                  variant={viewMode === 'editor' ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 text-xs ${viewMode === 'editor' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-400 text-emerald-900 hover:bg-emerald-50'}`}
                  onClick={() => setViewMode('editor')}
                  title="Open the Word-style WYSIWYG editor — edit text directly on the page."
                >
                  <Edit3 className="h-3 w-3 mr-1" />
                  Manual Edit (Word view)
                </Button>
                <Button
                  variant={viewMode === 'preview' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setViewMode('preview')}
                  disabled={!fileUrl}
                  title={!fileUrl ? 'No document URL available for preview' : 'View formatted Word document'}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Preview
                </Button>
              </div>}

              {/* Undo / Redo — always available, including in Preview mode
                  (an undo from Preview auto-saves & re-renders the Word doc
                  so the preview iframe reflects the restored content). */}
              <div className="w-px h-6 bg-border mx-1" />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleUndo}
                disabled={isSaving || (isResumeView ? jsonPast.length === 0 : undoStack.length === 0 && !tipTapCanUndo)}
                title={`Undo (Ctrl+Z)${viewMode === 'preview' ? ' — will auto-refresh preview' : ''}`}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleRedo}
                disabled={isSaving || (isResumeView ? jsonFuture.length === 0 : redoStack.length === 0 && !tipTapCanRedo)}
                title={`Redo (Ctrl+Y)${viewMode === 'preview' ? ' — will auto-refresh preview' : ''}`}
              >
                <Redo2 className="h-4 w-4" />
              </Button>

              {viewMode === 'editor' && (
                <>
                  <div className="w-px h-6 bg-border mx-1" />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('bold')} title="Bold (Ctrl+B)">
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('italic')} title="Italic (Ctrl+I)">
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('underline')} title="Underline (Ctrl+U)">
                    <Underline className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-border mx-1" />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('insertUnorderedList')} title="Bullet List">
                    <List className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('insertOrderedList')} title="Numbered List">
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-border mx-1" />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('justifyLeft')}>
                    <AlignLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('justifyCenter')}>
                    <AlignCenter className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => formatText('justifyRight')}>
                    <AlignRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              
              <div className="flex-1" />
              
              <div className="flex items-center gap-2">
                {!isResumeView && <Badge variant="secondary" className="text-xs">{wordCount} words</Badge>}
                {isResumeView && typeof pageCount === 'number' && <Badge variant="secondary" className="text-xs">{liveJson?.ResumeLayout && Object.keys(liveJson.ResumeLayout).some((k) => k !== 'sectionOrder' && k !== 'headingRenames' && k !== 'textStyles') ? 'About ' : ''}{pageCount} {pageCount === 1 ? 'page' : 'pages'}</Badge>}
                {isResumeView && saveState === 'pending' && <Badge variant="outline" className="text-xs">Saving soon</Badge>}
                {isResumeView && saveState === 'saving' && <Badge variant="outline" className="text-xs">Saving...</Badge>}
                {isResumeView && saveState === 'saved' && <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300">All changes saved</Badge>}
                {isResumeView && saveState === 'error' && <Badge variant="outline" className="text-xs text-red-700 border-red-300">Not saved. Please try again</Badge>}
                {hasUnsavedChanges && (
                  <Badge variant="outline" className="text-xs text-warning border-warning/50">Unsaved changes</Badge>
                )}
                {pendingAiEdit && (
                  <Badge variant="outline" className="text-xs text-amber-700 border-amber-400 bg-amber-50">
                    AI proposal pending
                  </Badge>
                )}
                {/* Editor Settings — AI approval mode */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Editor settings">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 bg-white">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm mb-1">Editor settings</h4>
                        <p className="text-xs text-muted-foreground">
                          Control how AI changes are applied to your document.
                        </p>
                      </div>
                      <div className="flex items-start justify-between gap-3 p-3 rounded-md border bg-muted/30">
                        <div className="flex-1">
                          <Label htmlFor="ai-auto-apply" className="text-sm font-medium cursor-pointer">
                            Auto-apply AI changes
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {aiAutoApply
                              ? 'AI edits are applied immediately when ready. You can still Undo (Ctrl+Z).'
                              : 'AI proposes changes and waits for you to Apply or Reject before anything is written.'}
                          </p>
                        </div>
                        <Switch
                          id="ai-auto-apply"
                          checked={aiAutoApply}
                          onCheckedChange={setAiAutoApply}
                        />
                      </div>
                      <div className="flex items-start justify-between gap-3 p-3 rounded-md border bg-muted/30">
                        <div className="flex-1">
                          <Label htmlFor="auto-format-on-save" className="text-sm font-medium cursor-pointer">
                            Auto-format on save
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {autoFormatOnSave
                              ? 'Every save first runs the resume formatter (highlights → 2 lines, skills → clean grid).'
                              : 'Run the formatter only when you click the "Format Resume" button.'}
                          </p>
                        </div>
                        <Switch
                          id="auto-format-on-save"
                          checked={autoFormatOnSave}
                          onCheckedChange={setAutoFormatOnSave}
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Content Area - Editor or Preview */}
            {isResumeView && leftView === 'page' ? (
              <PageEditor json={liveJson} templateId={docmosisTemplateId} onChange={commitJson} />
            ) : viewMode === 'editor' ? (
              <div className="flex-1 overflow-auto">
                {/* Word-letter WYSIWYG editor (TipTap/ProseMirror). Looks
                    like an 8.5×11 page, styled to the chosen Docmosis
                    template family. Reports plain text upstream so the
                    AI / JSON / Docmosis pipeline is unchanged. */}
                {isLoadingDocHtml && !docHtml ? (
                  <div className="flex items-center justify-center h-32 text-sm text-muted-foreground gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading the Word document into the editor…
                  </div>
                ) : null}
                <TipTapResumeEditor
                  ref={tiptapRef}
                  initialText={editedContent}
                  initialHtml={docHtml}
                  templateFamily={templateFamily}
                  onChange={(text) => {
                    if (!isApplyingHistoryRef.current) {
                      if (typingDebounceRef.current) window.clearTimeout(typingDebounceRef.current);
                      const beforeEdit = editedContent;
                      typingDebounceRef.current = window.setTimeout(() => {
                        pushHistory(beforeEdit);
                      }, 600);
                    }
                    setEditedContent(text);
                    setHasUnsavedChanges(true);
                    onContentUpdate(text);
                  }}
                  onHistoryChange={(canUndo, canRedo) => {
                    setTipTapCanUndo(canUndo);
                    setTipTapCanRedo(canRedo);
                  }}
                  className="h-full"
                />
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                {fileUrl ? (
                  <WordOnlineViewer
                    documentUrl={fileUrl}
                    fileName={fileName || 'Document'}
                    height="100%"
                    className="h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <p>No document URL available for preview</p>
                  </div>
                )}
              </div>
            )}

            {/* Bottom Action Bar */}
            <div className="border-t bg-muted/30 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!isResumeView && <Button variant="ghost" size="sm" onClick={handleResetContent} disabled={!hasUnsavedChanges}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>}
                {/* Format Resume — runs the per-template rules engine:
                    highlights → exactly 2 lines, skills → clean grid, summary
                    layout check. Auto-saves & refreshes the preview. */}
                {false && <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runResumeFormatter()}
                  disabled={isFormatting || isSaving || !documentJson}
                  className="gap-2 border-emerald-400 text-emerald-900 hover:bg-emerald-50 hover:border-emerald-500"
                  title="Apply the resume formatting rules: highlights → 2 lines, skills → clean grid, summary check."
                >
                  {isFormatting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  Format Resume
                </Button>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close Editor
                </Button>
                {onSaveAndRegenerate && !isResumeView && (
                  <Button size="sm" onClick={handleSaveAndRegenerate} disabled={isSaving || isFormatting || !hasUnsavedChanges}>
                    {isSaving ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-2" /> Save & Update Word Doc</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right Panel - AI Sidebar (the dead-end "Manual Edit" tab that
            used to live here was just a tips/stats panel, not an editor.
            Removed — the real manual editor is the WYSIWYG page on the left
            via the "Manual Edit (Word view)" toggle in the toolbar). */}
        <ResizablePanel defaultSize={40} minSize={25} maxSize={50}>
          <div className="h-full flex flex-col border-l bg-background">
            <Tabs defaultValue="ai" className="h-full flex flex-col">
              <div className="px-4 pt-4">
                <TabsList className={`grid w-full ${isResumeView ? 'grid-cols-4' : 'grid-cols-1'}`}>
                  <TabsTrigger value="ai" className="text-xs">
                    <Wand2 className="w-3 h-3 mr-1" />
                    AI Assistant
                  </TabsTrigger>
                  {isResumeView && (
                    <TabsTrigger value="fit" className="text-xs">
                      <Ruler className="w-3 h-3 mr-1" />
                      Fine-Tune
                    </TabsTrigger>
                  )}
                  {isResumeView && <TabsTrigger value="layout" className="text-xs">Layout</TabsTrigger>}
                  {isResumeView && <TabsTrigger value="history" className="text-xs">History</TabsTrigger>}
                </TabsList>
              </div>

              {isResumeView && (
                <TabsContent value="fit" className="flex-1 overflow-y-auto p-4">
                  <FineTunePanel
                    resumeJson={liveJson}
                    templateId={docmosisTemplateId}
                    onApply={(next) => autoSaveWithText(editedContent, { silent: true, presetJson: next })}
                  />
                </TabsContent>
              )}
              {isResumeView && (
                <TabsContent value="layout" className="flex-1 overflow-y-auto p-4">
                  <LayoutPanel resumeJson={liveJson} onApply={(next) => autoSaveWithText(editedContent, { silent: true, presetJson: next })} />
                </TabsContent>
              )}
              {isResumeView && (
                <TabsContent value="history" className="flex-1 overflow-y-auto p-4">
                  <HistoryPanel rowId={tailoredRowId} refreshKey={fileUrl} onRestore={(json) => autoSaveWithText(editedContent, { silent: true, presetJson: json })} />
                </TabsContent>
              )}

              {/* AI Assistant Tab */}
              <TabsContent value="ai" className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Quick Actions Grid */}
                <div>
                  <h4 className="text-sm font-semibold mb-3 text-foreground">Quick Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {predefinedActions.map((action) => (
                      <Button
                        key={action.id}
                        variant="outline"
                        className="h-auto p-3 flex flex-col items-center gap-2 text-center"
                        onClick={() => action.id === 'SMART_FORMATTING' ? handleSmartFormat() : processAIEdit(action.id)}
                        disabled={isProcessing}
                      >
                        <div className={`w-7 h-7 ${action.color} rounded-full flex items-center justify-center text-white`}>
                          {action.icon}
                        </div>
                        <div className="text-xs font-medium leading-tight">{action.label}</div>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Custom Prompt */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-foreground">Tell the AI what to change</h4>
                  {chatHistory.length > 0 && (
                    <div className="mb-2 max-h-56 overflow-y-auto space-y-2 rounded-md border bg-muted/20 p-2">
                      {chatHistory.map((m, i) => (
                        <div key={i} className="space-y-1">
                          <div className="ml-6 rounded-md bg-primary/10 px-2 py-1 text-xs text-foreground">{m.request}</div>
                          <div className="mr-6 rounded-md bg-background px-2 py-1 text-xs text-muted-foreground border">{m.reply}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Textarea
                      placeholder={speechSupported
                        ? "Type or tap the mic and say what you want, e.g. 'Make the second highlight about the $5M savings' or 'Shorten the summary'"
                        : "Type what you want, e.g. 'Make the second highlight about the $5M savings' or 'Shorten the summary'"}
                      value={customRequest}
                      onChange={(e) => setCustomRequest(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && customRequest.trim() && !isProcessing) {
                          e.preventDefault();
                          processAIEdit(customRequest.trim());
                        }
                      }}
                      className="min-h-[100px] text-sm pr-12"
                      disabled={isProcessing}
                    />
                    {speechSupported && (
                      <Button
                        type="button"
                        size="icon"
                        variant={isListening ? 'destructive' : 'outline'}
                        className="absolute right-2 bottom-2 h-8 w-8 rounded-full"
                        onClick={toggleListening}
                        disabled={isProcessing}
                        title={isListening ? 'Stop listening' : 'Talk to the AI'}
                      >
                        {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                  {isListening && <p className="mt-1 text-xs text-red-600">Listening… speak your change, then tap the mic or press Apply.</p>}
                  <Button
                    onClick={() => processAIEdit(customRequest.trim())}
                    disabled={isProcessing || !customRequest.trim()}
                    className="w-full mt-2"
                  >
                    {isProcessing ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <><Wand2 className="w-4 h-4 mr-2" /> Apply AI Changes</>
                    )}
                  </Button>
                </div>

                {/* Processing State */}
                {isProcessing && (
                  <Card className="p-4 text-center bg-muted/30">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">{processingNote || 'Working on it…'}</p>
                    <p className="text-xs text-muted-foreground">Usually 20 to 60 seconds</p>
                  </Card>
                )}

                {/* Pending AI proposal — shown when approval mode is on. The
                    proposed text is held aside until the user clicks Apply
                    (writes to editor + snapshots undo) or Reject (discards). */}
                {pendingAiEdit && !isProcessing && (
                  <Card className="p-4 bg-amber-50 border-amber-300 space-y-3">
                    <div className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-900">AI changes awaiting your approval</span>
                    </div>
                    <p className="text-sm text-amber-900">{pendingAiEdit.summary}</p>
                    {pendingAiEdit.reasoning && (
                      <details className="text-xs text-amber-800">
                        <summary className="cursor-pointer hover:text-amber-950">View reasoning</summary>
                        <p className="mt-2 pl-2 border-l-2 border-amber-300 text-xs">{pendingAiEdit.reasoning}</p>
                      </details>
                    )}
                    <details className="text-xs">
                      <summary className="cursor-pointer text-amber-800 hover:text-amber-950">
                        Preview proposed text ({pendingAiEdit.proposedContent.length.toLocaleString()} chars)
                      </summary>
                      <div className="mt-2 max-h-48 overflow-auto p-2 bg-white rounded border border-amber-200 whitespace-pre-wrap text-xs text-foreground">
                        {pendingAiEdit.proposedContent}
                      </div>
                    </details>
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-1"
                        onClick={acceptPendingAiEdit}
                      >
                        <Check className="w-4 h-4" />
                        Apply Changes
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-400 text-amber-900 hover:bg-amber-100 gap-2 flex-1"
                        onClick={rejectPendingAiEdit}
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </Button>
                    </div>
                    <p className="text-[10px] text-amber-700">
                      Tip: enable "Auto-apply AI changes" in editor settings (⚙) to skip this step.
                    </p>
                  </Card>
                )}

                {/* Last Edit Summary */}
                {lastEdit && !isProcessing && !pendingAiEdit && (
                  <Card className="p-4 bg-muted/30 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-semibold">Last Edit</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{lastEdit.summary}</p>
                    {lastEdit.questions && lastEdit.questions.length > 0 && (
                      <div className="text-xs space-y-1">
                        <p className="font-semibold text-foreground">The AI needs these real facts to go further (answer in the box above):</p>
                        <ul className="list-disc pl-4 text-muted-foreground">
                          {lastEdit.questions.map((q, i) => <li key={i}>{q}</li>)}
                        </ul>
                      </div>
                    )}
                    {lastEdit.reasoning && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer hover:text-foreground">View reasoning</summary>
                        <p className="mt-2 pl-2 border-l-2 border-muted text-xs">{lastEdit.reasoning}</p>
                      </details>
                    )}
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
